<script setup>
import AncientStudentService from "@/services/AncientStudentService"
import Step from "@/views/survey/Step.vue"
import FooterBlank from "@/layouts/components/FooterBlank.vue"
import ContentError from "@/components/common/ContentError.vue"

import { langTypes } from "@/utils/constant"
import { useSurveyHandle } from "@/composables/useSurvey"
import { useAuthStore } from "@/stores/authStore"
import { useRoute } from "vue-router"
import { router } from "@/plugins/1.router"

// ==================== START VARIABLE ======================
const CURRENT_YEAR = import.meta.env.VITE_CURRENT_YEAR
const { params } = useRoute()
const {
  dataSurvey,
  computedSteps,
  errorData,
  loading,
  buttonLoading,

  showCustomSwal,
} = useSurveyHandle("AncientStudent", AncientStudentService, params.id)

definePage({
  meta: {
    layout: "blank",
    public: true,
  },
})

const dataInfoBlocksAncientStudent = [
  {
    id: 1,
    type: "alert",
    position: "top",
    content: `
    <p class="text-error">${$gettext(
      "Attention, si vous passez plus de 30 minutes sur cette page ou que vous laissez cet onglet ouvert trop longtemps, votre session va expirer et vos réponses ne seront pas enregistrées."
    )}
    </p>
    <p class="text-error">${$gettext(
      "Si vous ne recevez pas de mail de confirmation après avoir validé votre formulaire, c'est que vos réponses n'ont pas été prises en compte."
    )}
    </p>
    <p class="text-error">${$gettext(
      "N'hésitez pas à nous contacter en cas de doute ou de difficulté."
    )}
    </p>
    `,
  },
  {
    id: 2,
    type: "alert",
    position: "bottom",
    content: `
    <p class="text-error">${$gettext(
      "Si vous n’avez pas suivi ce programme : ne répondez pas au formulaire et contactez-nous au plus vite en nous envoyant un email sur : "
    )}
      
    <a class="text-blue" href="mailto:classements.france@eduniversal-group.com"><u>classements.france@eduniversal-group.com</u></a>
    ${$gettext("ou un sms / whatsapp au ")}
    <a class="text-blue" href="tel:0630231266"><u>06 30 23 12 66</u></a> <span class="text-sky">/</span> <a class="text-blue" href="tel:0644154471"><u>06 44 15 44 71</u></a>
    </p>
    `,
  },
]
// ==================== START METHODS ======================
const onChangeLang = (lang) => {
  const locale = langTypes.find((x) => x.id === lang)
  if (locale) {
    const urlObj = new URL(window.location.href)
    urlObj.searchParams.set("lang", locale.name)
    const newUrl = urlObj.toString()
    window.location.href = newUrl
  }
}
async function onSubmit(obj) {
  try {
    buttonLoading.value = true
    obj.hash = params.id
    const response = await AncientStudentService.postSurveyByStep({
      ...obj,
    })

    if (response) {
      await showCustomSwal({
        html: `
        <h3 class="text-center text-primary mb-2">${$gettext(
          "Formulaire validé."
        )}</h3>
        <p class="text-center mb-0">${$gettext(
          "Merci pour votre participation !"
        )}</p>
        `,
        showCancelButton: false,
        showConfirmButton: false,
        allowOutsideClick: false,
        showCloseButton: true,
        timer: 10000,
        width: 490,
        position: "center",
        timerProgressBar: true,
        willClose: () => {
          router.push({ name: "login" })
        },
      })
    }
  } catch (error) {
    errorData.value = error?.data
  } finally {
    buttonLoading.value = false
  }
}

const route = useRoute()
const campaign = ref(import.meta.env.VITE_CAMPAIGN === 'true')
const isAdmin = computed(() => route.query.isAdmin)
const authStore = useAuthStore()

watch(() => campaign.value, async (newVal) => {
  if (!newVal) {
    if (isAdmin.value != 1) {
      await authStore.logout()
    }
  }
}, {immediate: true})
// ==================== END METHODS ========================
</script>

<template>
  <div class="register-student" style="min-height: 100vh">
    <div class="register-student__container">
      <header
        class="d-flex align-center justify-center px-6"
        style="position: relative"
      >
        <RouterLink to="/" class="app-logo app-title-wrapper">
          <img src="@/assets/logo.png" alt="Logo" />
        </RouterLink>
        <h1 class="title">
          {{ $gettext("Enquêtes France") }} {{ CURRENT_YEAR }}
        </h1>
        <BtnLang @onChange="onChangeLang" />
      </header>
      <main
        :class="[
          errorData?.code ? 'layout-error' : '',
          'register-student__main',
        ]"
      >
        <div style="max-width: 100%; margin: 0 auto; padding: 16px 0">
          <VCard class="form">
            <ContentError v-if="errorData?.code" :code="errorData?.code" />
            <template v-else>
              <div
                v-for="(item, index) in computedSteps"
                :key="item.id"
                :value="index"
              >
                <h1 style="font-weight: 800" class="text-center">
                  {{ dataSurvey.formType?.name }}
                </h1>
                <InfoBlock
                  v-for="item in dataInfoBlocksAncientStudent.filter(
                    (item) => item.position === 'top'
                  )"
                  :key="item.id"
                  :infoBlock="item"
                  class="my-3"
                />
                <h1 class="text-center">
                  {{ dataSurvey.program?.school?.name }}
                </h1>
                <h1 class="text-second-primary text-center">
                  {{ dataSurvey.program?.name }}
                </h1>

                <InfoBlock
                  v-for="item in dataInfoBlocksAncientStudent.filter(
                    (item) => item.position === 'bottom'
                  )"
                  :key="item.id"
                  :infoBlock="item"
                  class="my-3"
                />
                <div class="survey__main mt-3">
                  <div class="d-flex gap-6 flex-row-reverse survey__company">
                    <VCard class="flex-1 px-6 py-6">
                      <Waiting :show="loading" :isBlurFullScreen="false" />
                      <div
                        class="form-survey"
                        transition="fade-transition"
                        reverse-transition="fade-transition"
                      >
                        <h4 class="form-survey__title">
                          {{ item.title }}
                        </h4>
                        <!-- <pre>{{ item }}</pre> -->
                        <hr v-if="item.title" class="space" />

                        <!-- question -->
                        <Step
                          :step="item"
                          :loading="buttonLoading"
                          :survey="dataSurvey"
                          :isAncientStudent="true"
                          @update:modelValue="onSubmit($event)"
                        />
                      </div>
                    </VCard>
                  </div>
                </div>
              </div>
            </template>
          </VCard>
        </div>
      </main>
    </div>

    <VCard style="margin: 2rem 0" v-if="!errorData?.code">
      <ContactWithUs style="margin-bottom: 5rem" />
    </VCard>
    <FooterBlank :footerRh="true" />
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/pages/register-student.scss";
.register-recruiter__header {
  gap: 10px;
}
.survey {
  background-color: rgba(var(--v-theme-primary));
}

.print-pdf {
  padding: 12px 16px;
  max-width: 168px;
  height: fit-content;
}
.layout-error {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
// .title-survey {
//   font-size: 20px;
//   font-weight: 700;
// }
</style>
